import {
  COLUMN_DEFINITIONS,
  COLUMNS_BY_GENERAL_SETTINGS_TYPE,
} from '@work4all/models';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { LookupType } from '@work4all/models/lib/Enums/LookupType.enum';

import {
  hexToSignedInt,
  signedIntToHex,
} from '@work4all/utils/lib/colorConversion';

import { ControllerPlusRenderProps } from '../containers/mask-overlays/form-plus/controller-plus';
import { useFormContextPlus } from '../containers/mask-overlays/form-plus/use-form-context-plus';
import { GeneralSettingsMaskFormValue } from '../containers/mask-overlays/mask-overlay/views/general-settings/types';

export const ENTITY_VARIANT_TO_LOOKUP_TYPE: Record<string, LookupType> = {
  ticketCategory2: LookupType.TICKET_KATEGORIE_2,
  ticketCategory1: LookupType.TICKET_KATEGORIE_1,
  projectKind: LookupType.PROJEKT_TYPE,
  projectStatus: LookupType.PROJEKT_STATI,
  addressLinks: LookupType.ADRESSVERKNUEPFUNG,
  relations: LookupType.BEZIEHUNGEN,
  ticketChecklist: LookupType.TICKET_CHECKLISTENPUNKT,
  branchOffices: LookupType.NIEDERLASSUNGEN,
  resourceClass: LookupType.RESSOURCENKLASSEN,
};

export const CONTROLLER_DEFINITIONS = {
  [Entities.ticketKind]: {
    color: {
      type: 'color',
      makeProps: (
        field: ControllerPlusRenderProps<GeneralSettingsMaskFormValue>,
        form: ReturnType<
          typeof useFormContextPlus<GeneralSettingsMaskFormValue>
        >
      ) => {
        return {
          onChange: (e) => {
            form.setValue('color', hexToSignedInt(e.target.value), {
              shouldValidate: true,
              shouldDirty: true,
            });
          },

          value: signedIntToHex(field.value as string),
        };
      },
    },
    minutesPerTicket: {
      type: 'number',
      useRegister: true,
    },
  },
};

interface TableColumn {
  title: string;
  displayTitle: string;
  accessor: string;
  accessorDE: string;
  filterField: string;
  filterFieldDE: string;
  idDE: string;
  id: string;
  path: unknown[];
  width: number;
  sortable: boolean;
  groupable: boolean;
  quickSearchable: boolean;
  defaultHidden: boolean;
  filterable: {
    type: string;
    primary: boolean;
    params: Record<string, unknown>;
  };
  disableFooterSum: boolean;
}

interface TableSchema {
  entityNameDE?: string;
  entity?: string;
  columns: TableColumn[];
  [key: string]: unknown;
}

/**
 * Extends a general settings schema with entity-specific columns
 * @param baseSchema - The base schema to extend
 * @param entityType - The entity type to get columns for
 * @returns Extended schema with additional columns
 */
export function extendGeneralSettingsSchema(
  baseSchema: TableSchema,
  entityType: Entities
): TableSchema {
  const additionalColumns = COLUMNS_BY_GENERAL_SETTINGS_TYPE[entityType];

  if (!additionalColumns || additionalColumns.length === 0) {
    return baseSchema;
  }

  const extendedSchema: TableSchema = {
    ...baseSchema,
    columns: [
      ...baseSchema.columns,
      ...additionalColumns.map(
        (columnName): TableColumn => ({
          title: `COMMON.${columnName.toUpperCase()}`,
          displayTitle: columnName,
          accessor: columnName,
          accessorDE: columnName,
          filterField: columnName,
          filterFieldDE: columnName,
          idDE: columnName,
          id: columnName,
          path: [],
          width: 150,
          sortable: true,
          groupable: false,
          quickSearchable: false,
          defaultHidden: false,
          filterable: {
            type: 'None',
            primary: false,
            params: {},
          },
          disableFooterSum: true,
          ...COLUMN_DEFINITIONS[entityType][columnName],
        })
      ),
    ],
  };

  return extendedSchema;
}

export function mapEntityVariantToLookupType(
  entityVariant?: string | null
): LookupType | null {
  if (!entityVariant) {
    return null;
  }

  return ENTITY_VARIANT_TO_LOOKUP_TYPE[entityVariant] ?? null;
}
